import { Services } from 'shared/domain/messages/message';
import type {
  ProjectCreateRequest,
  ProjectEditRequest,
} from 'shared/domain/messages/project/eventMessages';
import { broadcastAllProjects } from 'serviceWorker/broadcasts/project/all';
import * as projects from 'serviceWorker/db/projects/projects';
import * as projectsService from 'serviceWorker/db/projects/projectsService';
import { debugLog } from 'shared/logger/debugLog';
import { createSyncedEntity } from '../factories/createSyncedEntity';
import { projectSaver } from './addProject';
import { projectEditor } from './editProject';
import { fetchProjects } from './fetchProjects';
import { projectContract } from 'shared/domain/project/projectContract';

/**
 * The user's projects, kept across project switches. They have no sync key:
 * `sync` downloads the list again (the stored list stays readable meanwhile),
 * `downloadWhenMissing` downloads only a missing one and publishes nothing for
 * a downloaded one: publishing would resync the selected project, which reads
 * the list again (a loop).
 */
export const projectsSync = createSyncedEntity({
  entityName: Services.PROJECTS,
  contract: projectContract,
  repository: projects,
  service: projectsService,
  fetch: fetchProjects,
  publishCollection: broadcastAllProjects,
  requiredConfig: ['api'],
  keepStoredWhileDownloading: true,
});

/**
 * Resolves once the change is in the HTTP queue and rejects only when it could
 * not be queued. The server's answer follows as an event (`createdEntity`,
 * `updatedEntity`, `failUploadedEntity`) with the change's `uniqueId`.
 */
export function handleCreateProject(detail: ProjectCreateRequest): Promise<void> {
  debugLog('addProject', detail);
  return projectSaver.execute(detail.projectCreateModel, detail.uniqueId);
}

export function handleEditProject(detail: ProjectEditRequest): Promise<void> {
  debugLog('editProject', detail);
  return projectEditor.execute({
    projectEditModel: detail.projectEditModel,
    uniqueId: detail.uniqueId,
    projectId: detail.projectEditModel._id,
  });
}

